import { NavLink } from "react-router-dom";
import { LayoutGrid, ClipboardList, Users, LogOut, Languages, BarChart3, ArchiveRestore, Undo2, Boxes } from "lucide-react";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { Button } from "./ui/button";
import { ChangePasswordDialog } from "./ChangePasswordDialog";
import { BranchSwitcher } from "./BranchSwitcher";

const NavItem = ({ to, icon: Icon, label, id }) => (
  <NavLink to={to} end data-testid={id}
    className={({ isActive }) => `shrink-0 whitespace-nowrap flex items-center gap-2 px-3 py-2 rounded-md text-sm font-semibold transition-colors ${isActive ? "bg-[#3E3E40] text-white" : "text-slate-600 hover:bg-slate-200 hover:text-slate-900"}`}>
    <Icon className="w-4 h-4" /> <span className="hidden sm:inline">{label}</span>
  </NavLink>
);

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useI18n();
  return (
    <div className="min-h-screen">
      <header className="no-print sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b">
        <div className="max-w-[1500px] mx-auto px-4 md:px-8 min-h-16 py-2 flex flex-wrap 2xl:flex-nowrap items-center gap-x-4 gap-y-2">
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="" data-testid="app-logo" className="w-10 h-10 rounded-md border bg-white object-contain" />
            <div className="leading-tight hidden md:block">
              <div className="font-extrabold">{t("appName")}</div>
            </div>
          </div>
          <nav className="order-last w-full 2xl:order-none 2xl:w-auto flex items-center gap-1 2xl:ms-2 overflow-x-auto 2xl:overflow-visible">
            <NavItem to="/" icon={LayoutGrid} label={t("shelves")} id="nav-shelves" />
            <NavItem to="/warehouse" icon={Boxes} label={t("warehouse")} id="nav-warehouse" />
            <NavItem to="/records" icon={ClipboardList} label={t("records")} id="nav-records" />
            <NavItem to="/reports" icon={BarChart3} label={t("reports")} id="nav-reports" />
            <NavItem to="/returns" icon={Undo2} label={t("returnsNav")} id="nav-returns" />
            {user.role === "admin" && <NavItem to="/employees" icon={Users} label={t("employees")} id="nav-employees" />}
            {user.role === "admin" && <NavItem to="/deleted-files" icon={ArchiveRestore} label={t("deletedFiles")} id="nav-deleted-files" />}
          </nav>
          <div className="ms-auto flex items-center gap-2">
            <BranchSwitcher />
            <span className="hidden 2xl:inline whitespace-nowrap text-sm text-slate-600" data-testid="current-user-name">{user.name} · {t(user.role)}</span>
            <Button variant="outline" size="sm" data-testid="lang-toggle" onClick={() => setLang(lang === "ar" ? "en" : "ar")}>
              <Languages className="w-4 h-4" /> {lang === "ar" ? "EN" : "ع"}
            </Button>
            <ChangePasswordDialog />
            <Button variant="outline" size="sm" className="text-rose-600 border-rose-200 hover:bg-rose-50 hover:text-rose-700" data-testid="logout-btn" onClick={logout}><LogOut className="w-4 h-4" /> <span>{t("logout")}</span></Button>
          </div>
        </div>
      </header>
      <main className="max-w-[1500px] mx-auto px-4 md:px-8 py-6 md:py-10">{children}</main>
    </div>
  );
};
